<template>
    <div :class="{ 'container form-container': !embedded }">
        <page-header v-if="!embedded" :title="pageTitle" :subtitle="card.number || null" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="isReadOnly" @submit.prevent="onSubmit">
            <v-card :outlined="!embedded" :flat="embedded" :loading="loadingEntity">
                <v-card-text :class="{ 'pa-0': embedded }">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="card.number" :rules="required" label="Card number *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select v-model="card.type" :items="types" :rules="required" label="Card type *" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <date-field v-model="card.expirationDate" label="Expiration date" :readonly="isReadOnly" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="card.qr" label="QR code" outlined />
                        </v-col>
                        <v-col v-if="!embedded" cols="12">
                            <entity-search
                                v-model="card.member"
                                :search-fn="searchMembers"
                                :item-text="memberLabel"
                                :rules="required"
                                :readonly="isReadOnly"
                                label="Member *"
                                hint="Type a name or a card number"
                                prepend-icon="mdi-account-outline"
                            />
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex flex-wrap checkbox-row">
                                <v-checkbox v-model="card.active" label="Active" hide-details />
                                <v-checkbox v-model="card.vip" label="VIP" hide-details />
                                <v-checkbox v-model="card.reliable" label="Reliable" hide-details />
                                <v-checkbox v-model="card.exclusive" label="Exclusive" hide-details />
                                <v-checkbox v-model="card.gold" label="Gold" hide-details />
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <div v-if="!isReadOnly" class="form-actions" :class="{ 'form-actions--embedded': embedded }">
                <delete-button
                    v-if="embedded"
                    label="Remove card"
                    small
                    title="Remove this card?"
                    :text="card.id ? 'The card is deleted permanently.' : 'This card has not been saved yet.'"
                    @delete-entry="$emit('remove')"
                />
                <v-btn v-else text @click="goBack">Cancel</v-btn>
                <v-spacer />
                <v-btn color="primary" depressed :small="embedded" type="submit" :loading="saving" :disabled="!valid">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ embedded && !card.id ? 'Add card' : 'Save' }}
                </v-btn>
            </div>
        </v-form>
    </div>
</template>

<script>
    import cardService from '@/services/card-service'
    import memberService from '@/services/member-service'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import DeleteButton from '@/components/DeleteButton'
    import EntitySearch from '@/components/EntitySearch'
    import { entityId, memberLabel, pick, toInputDate } from '@/utils/format'

    const CARD_FIELDS = ['number', 'active', 'member', 'expirationDate', 'qr', 'type', 'vip', 'reliable', 'exclusive', 'gold']

    function emptyCard() {
        return { number: '', active: true, expirationDate: null, member: null, type: 'PARON', qr: '', vip: false, reliable: false, exclusive: false, gold: false }
    }

    export default {
        name: 'CardForm',
        components: { PageHeader, DateField, DeleteButton, EntitySearch },
        mixins: [formPage],
        props: {
            // set when the form is embedded in the member page
            memberId: {
                type: [Number, String],
                default: null
            },
            memberCard: {
                type: Object,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                entityLabel: 'Card',
                card: emptyCard(),
                types: ['PARON', 'My Market', 'AB']
            }
        },
        computed: {
            embedded() {
                return this.memberId !== null
            },
            isReadOnly() {
                return this.embedded ? this.readonly : this.readOnly
            }
        },
        created() {
            if (this.embedded) {
                this.setCard(this.memberCard || {})
                return
            }
            if (this.isCreate) {
                return
            }
            this.loadingEntity = true
            cardService.getCardById(this.$route.params.cardId).then(resp => {
                this.setCard(resp.data.data.card)
            }).catch(error => {
                this.$notify.error(error, 'Could not load the card')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            memberLabel,
            searchMembers: query => memberService.searchMembers(query),
            setCard(card) {
                this.card = { ...emptyCard(), ...card, expirationDate: toInputDate(card.expirationDate) }
            },
            buildPayload() {
                const payload = pick(this.card, CARD_FIELDS)
                payload.member = this.embedded ? this.memberId : entityId(this.card.member)
                return payload
            },
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = this.buildPayload()
                const request = this.card.id
                    ? cardService.editCardById(this.card.id, payload)
                    : cardService.createCard(payload)
                request.then(resp => {
                    if (!this.card.id && resp.data.id) {
                        this.card.id = resp.data.id
                    }
                    this.$notify.success(`Card ${this.card.number} saved`)
                    if (this.embedded) {
                        this.$emit('saved', { ...this.card })
                    } else {
                        this.goBack()
                    }
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the card')
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>

<style scoped>
.form-actions--embedded {
    position: static;
    padding-bottom: 0;
    background: transparent;
}
</style>
